import React from 'react';
import { Loader2 } from 'lucide-react';
import { hapticLight, hapticMedium } from '@/utils/haptic';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Disable haptic feedback for this button */
  noHaptic?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      className = '',
      onClick,
      type = 'button',
      noHaptic = false,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = {
      primary:
        'bg-primary text-white hover:bg-primary-dark focus:ring-primary shadow-sm hover:shadow-md active:scale-95',
      secondary:
        'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-gray-400 active:scale-95',
      danger:
        'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-sm hover:shadow-md active:scale-95',
      ghost:
        'bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-gray-300 active:scale-95',
      outline:
        'bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white focus:ring-primary active:scale-95',
    };

    // Touch targets: minimum 44x44px for WCAG 2.1 AA compliance
    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm min-h-[44px] min-w-[44px]',
      md: 'px-4 py-2 text-base min-h-[44px] min-w-[44px]',
      lg: 'px-6 py-3 text-lg min-h-[52px] min-w-[52px]',
    };

    const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

    const isDisabled = disabled || loading;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!noHaptic && !isDisabled) {
        // Use light haptic for ghost/outline, medium for others
        if (variant === 'ghost' || variant === 'outline') {
          hapticLight();
        } else {
          hapticMedium();
        }
      }
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        type={type}
        className={classes}
        disabled={isDisabled}
        onClick={handleClick}
        aria-busy={loading}
        aria-disabled={isDisabled}
        {...props}
      >
        {loading && (
          <Loader2
            className="animate-spin"
            size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16}
            aria-label="Chargement en cours"
          />
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
